import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Plus } from 'lucide-react';
import { api, errMsg, pad } from '../lib/api';
import AdminLogin from '../components/admin/AdminLogin';
import DoctrineEditor from '../components/admin/DoctrineEditor';
import ImportPanel from '../components/admin/ImportPanel';
import ApiAccess from '../components/admin/ApiAccess';
import DocumentsAdmin from '../components/admin/DocumentsAdmin';
import BookAdmin from '../components/admin/BookAdmin';

const STATUS_COLOR = { published: 'text-emerald-400', incoming: 'text-amber-300', draft: 'text-slate-500' };

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [panel, setPanel] = useState('editor');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/doctrines');
      setDocs(r.data);
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
    }
  }, []);

  useEffect(() => { document.title = 'Doctrine Vault — Admin'; }, []);
  useEffect(() => {
    if (authed === null) api.get('/admin/me').then(() => setAuthed(true)).catch(() => setAuthed(false));
    else if (authed) load();
  }, [authed, load]);

  const open = async (id) => {
    const r = await api.get(`/admin/doctrines/${id}`);
    setSelected(r.data);
    setPanel('editor');
  };

  const createBlank = async () => {
    try {
      const r = await api.post('/admin/doctrines', { title: `Untitled Doctrine ${pad(docs.length + 1)}`, status: 'draft' });
      await load();
      setSelected(r.data);
      setPanel('editor');
      toast.success('Doctrine created');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const logout = async () => { await api.post('/admin/logout').catch(() => {}); setAuthed(false); setSelected(null); };

  if (authed === null) return <div className="min-h-screen bg-[#02040a] flex items-center justify-center font-mono-precision text-xs tracking-[0.3em] text-slate-600">CHECKING SESSION…</div>;
  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#02040a] text-slate-100" data-testid="admin-page">
      <header className="sticky top-0 z-40 h-14 border-b border-white/[0.07] bg-[#02040a]/90 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse-dot" />
            <span className="font-celestial text-xs tracking-[0.25em]">DOCTRINE VAULT</span>
            <Link to="/" className="ml-4 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-cyan-300" data-testid="admin-view-site-link">← View site</Link>
          </div>
          <div className="flex items-center gap-2">
            {['editor', 'import', 'documents', 'book', 'api'].map((p) => (
              <button key={p} onClick={() => setPanel(p)} className={`font-mono-precision text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors ${panel === p ? 'border-amber-400/60 text-amber-300' : 'border-white/10 text-slate-500 hover:text-slate-300'}`} data-testid={`admin-panel-${p}`}>
                {p === 'api' ? 'AI / API access' : p === 'editor' ? 'Doctrines' : p}
              </button>
            ))}
            <button onClick={logout} className="ml-3 text-slate-500 hover:text-rose-400" data-testid="admin-logout-btn" aria-label="Log out"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className={`max-w-[1500px] mx-auto px-6 py-8 ${panel === 'documents' || panel === 'book' ? '' : 'grid lg:grid-cols-[320px_1fr] gap-8'}`}>
        {panel === 'documents' ? <DocumentsAdmin /> : panel === 'book' ? <BookAdmin /> : (
        <>
        <aside className="lg:sticky lg:top-22 self-start" data-testid="admin-doctrine-list">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500">{docs.length} doctrines</p>
            <button onClick={createBlank} className="flex items-center gap-1.5 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-amber-300 hover:text-amber-200" data-testid="admin-new-doctrine-btn"><Plus size={13} /> New</button>
          </div>
          <div className="space-y-1 max-h-[calc(100vh-10rem)] overflow-y-auto pr-1" data-lenis-prevent>
            {docs.map((d) => (
              <button key={d.id} onClick={() => open(d.id)} className={`w-full text-left rounded-xl px-4 py-3 border transition-colors ${selected?.id === d.id ? 'border-amber-400/50 bg-amber-400/[0.05]' : 'border-white/[0.06] hover:border-white/20'}`} data-testid={`admin-doctrine-item-${d.slug}`}>
                <div className="flex items-baseline gap-3">
                  <span className="font-celestial text-slate-500 text-sm">{pad(d.number)}</span>
                  <span className="text-sm text-slate-200 leading-snug flex-1">{d.title}</span>
                </div>
                <p className={`font-mono-precision text-[9px] uppercase tracking-[0.2em] mt-1.5 pl-8 ${STATUS_COLOR[d.status]}`}>{d.status} · {d.word_count} w</p>
              </button>
            ))}
          </div>
        </aside>

        <section className="min-w-0">
          {panel === 'import' && <ImportPanel docs={docs} onDone={async (d, kind) => { await load(); if (kind === 'document') { setPanel('documents'); return; } setSelected(d); setPanel('editor'); }} />}
          {panel === 'api' && <ApiAccess docs={docs} />}
          {panel === 'editor' && (selected ? (
            <DoctrineEditor key={selected.id} doc={selected} onSaved={async (d) => { setSelected(d); await load(); }} onDeleted={async () => { setSelected(null); await load(); }} />
          ) : (
            <div className="h-[60vh] flex flex-col items-center justify-center text-center border border-dashed border-white/[0.08] rounded-2xl" data-testid="admin-empty-state">
              <p className="font-celestial text-xl text-slate-300">Select a doctrine to edit</p>
              <p className="text-sm text-slate-500 mt-3 max-w-sm">Or import a .docx / .pdf / Google Doc from the Import tab. Titles and abstracts are detected automatically.</p>
            </div>
          ))}
        </section>
        </>
        )}
      </div>
    </div>
  );
}
